import { Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useSettings } from '../context/SettingsContext';
import { placeholderSvgDataUrl } from '../utils/placeholderImage';
import { API_BASE } from '../config/api';
import desktopHeroBanner from '../assets/hero banner/herobanner_desktop.png';
import mobileHeroBanner from '../assets/hero banner/herobanner_mobile.png';
import ProductCard from '../components/ProductCard';

const Home = () => {
  const { settings } = useSettings();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [dbCategories, setDbCategories] = useState([]);
  const [dbProducts, setDbProducts] = useState([]);
  const [dbMedia, setDbMedia] = useState([]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Fetch data from DB
    const fetchData = async () => {
      try {
        const [catRes, prodRes, mediaRes, galRes] = await Promise.all([
          fetch(`${API_BASE}/categories`),
          fetch(`${API_BASE}/products`),
          fetch(`${API_BASE}/media?limit=5`),
          fetch(`${API_BASE}/gallery?limit=5`)
        ]);
        const cats = await catRes.json();
        const prods = await prodRes.json();
        const media = await mediaRes.json();
        const gallery = await galRes.json();
        setDbCategories(Array.isArray(cats) ? cats : []);
        setDbProducts(Array.isArray(prods) ? prods : []);
        
        // Handle media response which has 'items' wrapper
        const mediaItems = media?.items || (Array.isArray(media) ? media : []);
        // Gallery images are the live project photos — show them first
        const galleryItems = (gallery?.items || []).map((g) => ({
          id: `g-${g.id}`,
          file_url: g.url,
          title: 'Project',
        }));
        setDbMedia([...galleryItems, ...mediaItems]);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    
    fetchData();
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileFilterOpen(false);
    }
  }, [isMobile]);

  const handleCategorySelect = async (categorySlug) => {
    setSelectedCategory(categorySlug);
    if (isMobile) {
      setIsMobileFilterOpen(false);
    }
    setLoadingProducts(true);

    try {
      const url = categorySlug === 'all' 
        ? `${API_BASE}/products` 
        : `${API_BASE}/products?category=${categorySlug}`;
      const res = await fetch(url);
      const data = await res.json();
      setDbProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching products by category:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const [loadingProducts, setLoadingProducts] = useState(false);

  const selectedCategoryData = useMemo(() => {
    if (selectedCategory === 'all') {
      return {
        name: 'All Products',
        description: 'Explore all products and discover our complete range of premium LED lighting solutions for every space and need. From energy-efficient panel lights to durable street lights, we offer cutting-edge illumination designed for residential, commercial, and industrial applications. Our collection is built with advanced LED technology to ensure long-lasting performance, lower energy consumption, and superior brightness. Whether you are looking for modern indoor fixtures or rugged outdoor lighting, we have the perfect solution to meet your requirements. Browse through our extensive range and find the ideal lighting products that combine innovation, quality, and affordability.'
      };
    }
    const cat = dbCategories.find(c => c.slug === selectedCategory);
    return cat ? {
      ...cat,
      description: cat.description || 'High-quality LED Lighting solutions designed for maximum efficiency, durability, and superior performance in any environment.'
    } : {
      name: 'Premium Lighting',
      description: 'High-quality LED Lighting solutions designed for maximum efficiency, durability, and superior performance in any environment.'
    };
  }, [selectedCategory, dbCategories]);

  const selectedProductDescriptions = useMemo(
    () => {
      if (selectedCategory === 'all') return [];

      return dbProducts
        .map((product) => ({
          id: product.id,
          description: String(product.description || '').trim(),
        }))
        .filter((product) => product.description);
    },
    [dbProducts, selectedCategory]
  );

  return (
    <div>
      {/* Hero Section */}
      <section className="hero hero-section hero-banner hero-wrapper" style={{ margin: 0, padding: 0 }}>
        <div className="hero-content" style={{ margin: 0, padding: 0 }}>
          {isMobile ? (
            <img
              src={mobileHeroBanner}
              alt="Hero"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          ) : (
            <div style={{ 
              position: 'relative',
              width: '100vw',
              aspectRatio: '21/9',
              borderRadius: '0',
              overflow: 'hidden',
              margin: 0,
              padding: 0,
              marginLeft: 'calc(-50vw + 50%)',
              marginRight: 'calc(-50vw + 50%)'
            }}>
              <img
                src={settings?.hero_image_url || desktopHeroBanner}
                alt="Hero"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </div>
          )}
        </div>
      </section>
      {/* Breadcrumbs & Header Section */}
      <section className="products-section" style={{ backgroundColor: 'var(--secondary)', padding: isMobile ? '5rem 0 1rem 0' : '5px 0 5px 0' }}>
        <div className="container">

          <h2 style={{ color: '#fff', fontSize: '3rem', fontWeight: 'bold', margin: '0 0 5px 0' }}>
            {selectedCategoryData.name}
          </h2>

          <div style={{ marginTop: '20px', marginBottom: '8px', maxWidth: '1100px' }}>
            {selectedProductDescriptions.length > 0 ? (
              <div style={{ display: 'grid', gap: '0.9rem' }}>
                {selectedProductDescriptions.map((product) => (
                  <div
                    key={product.id}
                    style={{
                      padding: '1rem 1.25rem',
                      border: '1px solid #222',
                      borderRadius: '12px',
                      backgroundColor: '#0d0d0d',
                    }}
                  >
                    <p
                      style={{
                        color: '#888',
                        lineHeight: '1.8',
                        fontSize: '1.02rem',
                        margin: 0,
                      }}
                    >
                      {product.description}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                padding: '1rem 1.25rem',
                border: '1px solid #222',
                borderRadius: '12px',
                backgroundColor: '#000',
              }}>
                <p style={{ 
                  color: '#888',
                  lineHeight: '1.8', 
                  fontSize: isMobile ? '0.7rem' : '0.85rem', 
                  fontWeight: isMobile ? '200' : '200',
                  textAlign: isMobile ? 'left' : 'justify',
                  margin: 0
                }}>
                  {selectedCategoryData.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Content: Categories above Products */}
      <section style={{ backgroundColor: 'var(--secondary)', paddingTop: '30px', paddingBottom: '30px' }}>
        <div className="container">
          {/* Categories as horizontal chips/pills above products */}
          <div style={{ marginBottom: '8px' }}>
            <style dangerouslySetInnerHTML={{ __html: `
              .home-cat-chip { padding: 0.6rem 1.2rem; border-radius: 50px; cursor: pointer; border: none; color: #aaa; font-size: 0.85rem; font-weight: 700; transition: all 0.25s ease; white-space: nowrap; background: rgba(255,255,255,0.06); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); flex-shrink: 0; }
              .home-cat-chip:hover { background: rgba(255,255,255,0.12); color: #ccc; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
              .home-cat-chip.active { color: #fff !important; transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.3); font-weight: 800; }
              .home-cat-chip.active.grad-0 { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important; }
              .home-cat-chip.active.grad-1 { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%) !important; }
              .home-cat-chip.active.grad-2 { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%) !important; }
              .home-cat-chip.active.grad-3 { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%) !important; }
              .home-cat-chip.active.grad-4 { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%) !important; }
              .home-cat-chip.active.grad-5 { background: linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%) !important; }
              .home-cat-chip.active.grad-6 { background: linear-gradient(135deg, #fccb90 0%, #d57eeb 100%) !important; }
              .home-cat-chip.active.grad-7 { background: linear-gradient(135deg, #96fbc4 0%, #f9f586 100%) !important; }
              .home-cat-chip.active.grad-8 { background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%) !important; }
              .home-cat-chip-scroll { position: relative; display: flex; flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; -webkit-overflow-scrolling: touch; gap: 0.5rem; padding: 0.25rem 1rem 0.5rem; scroll-snap-type: x mandatory; }
              .home-cat-chip-scroll::-webkit-scrollbar { display: none; }
              .home-cat-chip-scroll::after { content: ''; flex: 0 0 0.5rem; }
              @keyframes pulse-scroll { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
              .scroll-hint { display: none; align-items: center; gap: 0.3rem; justify-content: center; color: rgba(255,255,255,0.3); font-size: 0.65rem; margin-top: 0.35rem; animation: pulse-scroll 2s ease-in-out infinite; }
              @media (min-width: 769px) {
                .home-cat-chip-scroll { display: grid; grid-template-columns: repeat(5, 1fr); gap: 0.6rem; padding: 0; scroll-snap-type: none; }
                .home-cat-chip-scroll::after { display: none; }
                .home-cat-chip { width: 100%; text-align: center; padding: 0.6rem 0.8rem; }
                .scroll-hint { display: none !important; }
              }
              @media (max-width: 768px) {
                .home-cat-chip { font-size: 0.7rem !important; padding: 0.4rem 0.65rem !important; border-radius: 50px !important; width: auto !important; min-width: auto !important; min-height: auto !important; flex-shrink: 0; scroll-snap-align: start; }
                .home-cat-chip-scroll { gap: 0.4rem; padding: 0.15rem 0.5rem 0.4rem; }
                .scroll-hint { display: flex; }
              }
              @media (max-width: 400px) {
                .home-cat-chip { font-size: 0.64rem !important; padding: 0.35rem 0.5rem !important; border-radius: 50px !important; width: auto !important; min-width: auto !important; min-height: auto !important; }
                .home-cat-chip-scroll { gap: 0.3rem; padding: 0.1rem 0.4rem 0.35rem; }
              }
              @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
              @keyframes slideOutRight { from { transform: translateX(0); } to { transform: translateX(100%); } }
              @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
              @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
              .cat-drawer-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 2000; animation: fadeIn 0.3s ease; }
              .cat-drawer-overlay.closing { animation: fadeOut 0.3s ease forwards; }
              .cat-drawer { position: fixed; top: 0; right: 0; width: 300px; max-width: 85vw; height: 100vh; background: #0d0d0d; border-left: 1px solid #222; z-index: 2001; overflow-y: auto; padding: 1.5rem; animation: slideInRight 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94); box-shadow: -10px 0 40px rgba(0,0,0,0.5); }
              .cat-drawer.closing { animation: slideOutRight 0.3s cubic-bezier(0.55, 0.06, 0.68, 0.19) forwards; }
              .cat-drawer-close { position: absolute; top: 1rem; right: 1rem; background: none; border: none; color: #888; cursor: pointer; font-size: 1.5rem; padding: 0.3rem; line-height: 1; min-width: auto; min-height: auto; width: auto; }
              .cat-drawer-title { color: #fff; font-size: 1.1rem; font-weight: 700; margin-bottom: 1.2rem; }
              .cat-drawer-chip { display: block; width: 100% !important; padding: 0.7rem 1rem !important; border-radius: 50px !important; cursor: pointer; border: none; color: #999; font-size: 0.85rem; font-weight: 700; transition: all 0.25s ease; text-align: left; background: rgba(255,255,255,0.06); margin-bottom: 0.5rem; min-height: auto !important; min-width: auto !important; }
              .cat-drawer-chip:hover { background: rgba(255,255,255,0.12); color: #ccc; }
              .cat-drawer-chip.active { color: #fff !important; font-weight: 800; }
              .cat-drawer-chip.active.grad-0 { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important; }
              .cat-drawer-chip.active.grad-1 { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%) !important; }
              .cat-drawer-chip.active.grad-2 { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%) !important; }
              .cat-drawer-chip.active.grad-3 { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%) !important; }
              .cat-drawer-chip.active.grad-4 { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%) !important; }
              .cat-drawer-chip.active.grad-5 { background: linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%) !important; }
              .cat-drawer-chip.active.grad-6 { background: linear-gradient(135deg, #fccb90 0%, #d57eeb 100%) !important; }
              .cat-drawer-chip.active.grad-7 { background: linear-gradient(135deg, #96fbc4 0%, #f9f586 100%) !important; }
              .cat-drawer-chip.active.grad-8 { background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%) !important; }
              .filter-icon-btn { display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12); border-radius: 50%; width: 32px !important; height: 32px; cursor: pointer; color: #ccc; transition: all 0.2s ease; padding: 0 !important; min-width: 32px !important; min-height: 32px !important; flex-shrink: 0; }
              .filter-icon-btn:hover { background: rgba(255,255,255,0.15); color: #fff; }
              @media (max-width: 768px) { .filter-icon-btn { display: flex; } }
            ` }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', paddingTop: '0.8rem' }}>
              <h3 style={{ color: '#fff', fontSize: '1.2rem', fontWeight: '500', margin: 0 }}>Categories</h3>
              <button className="filter-icon-btn" onClick={() => setIsMobileFilterOpen(true)} title="Filter categories">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
              </button>
            </div>
            <div className="home-cat-chip-scroll">
              <button
                onClick={() => handleCategorySelect('all')}
                className={`home-cat-chip${selectedCategory === 'all' ? ' active grad-0' : ''}`}
              >
                All Products
              </button>
              {dbCategories.map((cat, i) => {
                const isActive = selectedCategory === cat.slug;
                const gradIdx = (i + 1) % 9;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat.slug)}
                    className={`home-cat-chip${isActive ? ` active grad-${gradIdx}` : ''}`}
                  >
                    {cat.name}
                  </button>
                );
              })}
            </div>
            <div className="scroll-hint">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              Scroll for more categories
            </div>
          </div>

          {/* Right-side sliding drawer for mobile categories filter */}
          {isMobileFilterOpen && (
            <>
              <div className="cat-drawer-overlay" onClick={() => setIsMobileFilterOpen(false)} />
              <div className="cat-drawer">
                <button className="cat-drawer-close" onClick={() => setIsMobileFilterOpen(false)}>&times;</button>
                <div className="cat-drawer-title">Categories</div>
                <button
                  onClick={() => { handleCategorySelect('all'); setIsMobileFilterOpen(false); }}
                  className={`cat-drawer-chip${selectedCategory === 'all' ? ' active grad-0' : ''}`}
                >
                  All Products
                </button>
                {dbCategories.map((cat, i) => {
                  const isActive = selectedCategory === cat.slug;
                  const gradIdx = (i + 1) % 9;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => { handleCategorySelect(cat.slug); setIsMobileFilterOpen(false); }}
                      className={`cat-drawer-chip${isActive ? ` active grad-${gradIdx}` : ''}`}
                    >
                      {cat.name}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Product Grid below categories */}
          {loadingProducts ? (
            <div style={{ color: 'var(--accent)', textAlign: 'center', padding: '3rem' }}>
              <div className="loading-spinner"></div>
              <p>Loading products...</p>
            </div>
          ) : dbProducts.length > 0 ? (
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: isMobile ? '0.8rem' : '1.25rem'
            }}>
              {dbProducts.map((product, idx) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  index={idx}
                />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              <p>No products found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose FACTO Section */}
      <section className="why-facto-section" style={{ backgroundColor: '#0a0a0a', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 900px 500px at 30% 50%, rgba(255,215,0,0.05), transparent 70%), radial-gradient(ellipse 700px 400px at 70% 20%, rgba(255,215,0,0.03), transparent 70%)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', zIndex: 1, padding: isMobile ? '1.5rem 0 1.5rem' : '40px 0' }}>
          <style dangerouslySetInnerHTML={{ __html: `
            .why-facto-title { font-size: clamp(1.8rem, 3vw, 2.5rem); font-weight: 900; color: #fff; text-align: center; margin: 0 0 8px; }
            .why-facto-title span { background: linear-gradient(135deg, #FFD700, #e6c200); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
            .why-facto-subtitle { font-size: clamp(0.85rem, 1.2vw, 1rem); color: rgba(255,255,255,0.5); text-align: center; max-width: 550px; margin: 0 auto 3rem; line-height: 1.6; }
            
            .why-facto-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
            .why-card { position: relative; background: linear-gradient(145deg, #111111, #0d0d0d); border: 1px solid rgba(255,255,255,0.06); border-radius: 20px; padding: 28px 20px 24px; text-align: center; transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1); cursor: default; overflow: hidden; }
            .why-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent, var(--card-accent, #FFD700), transparent); opacity: 0; transition: opacity 0.35s; }
            .why-card:hover { transform: translateY(-8px); border-color: rgba(255,215,0,0.2); box-shadow: 0 20px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,215,0,0.08); }
            .why-card:hover::before { opacity: 1; }
            
            .why-icon-wrap { width: 64px; height: 64px; border-radius: 18px; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; transition: transform 0.35s; }
            .why-card:hover .why-icon-wrap { transform: scale(1.1) rotate(-5deg); }
            .why-icon-gold { background: linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,215,0,0.05)); }
            .why-icon-blue { background: linear-gradient(135deg, rgba(79,172,254,0.15), rgba(79,172,254,0.05)); }
            .why-icon-green { background: linear-gradient(135deg, rgba(67,233,123,0.15), rgba(67,233,123,0.05)); }
            .why-icon-purple { background: linear-gradient(135deg, rgba(161,140,209,0.15), rgba(161,140,209,0.05)); }
            
            .why-card-title { color: #fff; font-size: 1.05rem; font-weight: 700; margin: 0 0 8px; }
            .why-card-desc { color: rgba(255,255,255,0.5); font-size: 0.82rem; line-height: 1.6; margin: 0; }
            .why-card-stat { display: inline-block; background: linear-gradient(135deg, #FFD700, #e6c200); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-size: 1.8rem; font-weight: 900; }
            
            @media (max-width: 1024px) { .why-facto-grid { grid-template-columns: repeat(2, 1fr); } }
            @media (max-width: 768px) { .why-facto-section { padding-top: 20px !important; } }
            @media (min-width: 769px) { .why-facto-section { padding: 0 !important; } }
            @media (max-width: 640px) { .why-facto-grid { grid-template-columns: 1fr; gap: 14px; } .why-card { padding: 22px 16px 20px; } }
          ` }} />
          
          <h2 className="why-facto-title">Why Choose <span>FACTO</span></h2>
          <p className="why-facto-subtitle">India's trusted LED lighting brand — delivering superior illumination with cutting-edge technology and unmatched reliability since day one.</p>
          
          <div className="why-facto-grid">
            <div className="why-card" style={{ '--card-accent': '#FFD700' }}>
              <div className="why-icon-wrap why-icon-gold">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <h3 className="why-card-title">Energy Efficient</h3>
              <p className="why-card-desc">Save up to <span className="why-card-stat">75%</span> on electricity bills with our advanced LED chip technology.</p>
            </div>
            
            <div className="why-card" style={{ '--card-accent': '#4facfe' }}>
              <div className="why-icon-wrap why-icon-blue">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4facfe" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <h3 className="why-card-title">Certified Quality</h3>
              <p className="why-card-desc">All products are <span className="why-card-stat">ISI & CE</span> certified, rigorously tested for safety and performance.</p>
            </div>
            
            <div className="why-card" style={{ '--card-accent': '#43e97b' }}>
              <div className="why-icon-wrap why-icon-green">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#43e97b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <h3 className="why-card-title">Long Warranty</h3>
              <p className="why-card-desc">Industry-leading <span className="why-card-stat">5 Year</span> warranty with pan-India service support network.</p>
            </div>
            
            <div className="why-card" style={{ '--card-accent': '#a18cd1' }}>
              <div className="why-icon-wrap why-icon-purple">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#a18cd1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                </svg>
              </div>
              <h3 className="why-card-title">Fast Delivery</h3>
              <p className="why-card-desc">Orders shipped within <span className="why-card-stat">24hrs</span>. Reliable delivery to 28,000+ pin codes across India.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Media Section */}
      <section className="home-media-section" style={{ backgroundColor: 'var(--primary)', padding: isMobile ? '' : '0' }}>
        <div style={{ padding: isMobile ? '1.5rem 0 2.5rem' : '40px 0' }}>
        <div className="container">
          <h2 className="section-title">Media Gallery</h2>
          <p className="section-subtitle">Live updated images from our projects</p>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', 
            gap: '1rem',
            marginBottom: '2.5rem'
          }}>
            {dbMedia.length > 0 ? (
              dbMedia.slice(0, 5).map((item, idx) => (
                <div key={item.id || idx} style={{ 
                  aspectRatio: '1/1', 
                  borderRadius: '12px', 
                  overflow: 'hidden',
                  border: '1px solid #333'
                }}>
                  <img 
                    src={item.file_url || placeholderSvgDataUrl({ width: 300, height: 300, text: 'Project' })} 
                    alt={item.title || 'Project'} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.currentTarget.parentNode.style.display = 'none'; }}
                  />
                </div>
              ))
            ) : (
              [...Array(5)].map((_, idx) => (
                <div key={idx} style={{ 
                  aspectRatio: '1/1', 
                  borderRadius: '12px', 
                  overflow: 'hidden',
                  border: '1px solid #333'
                }}>
                  <img 
                    src={placeholderSvgDataUrl({ width: 300, height: 300, text: 'Project Image' })} 
                    alt="Placeholder" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              ))
            )}
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <Link 
              to="/media" 
              className="btn"
              style={{ 
                display: 'inline-block',
                padding: '0.8rem 2rem',
                backgroundColor: 'var(--accent)',
                color: 'var(--primary)',
                borderRadius: '30px',
                fontWeight: 'bold',
                textDecoration: 'none',
                transition: 'all 0.3s ease'
              }}
            >
              View all images →
            </Link>
          </div>
          </div>
        </div>
      </section>
      <section className="home-testimonials-section">
        <div className="container">
          <h2 className="section-title">What Our Clients Say</h2>
          <p className="section-subtitle">Trusted by businesses across India</p>
          
          <div className="grid grid-3">
            <div className="card">
              <div style={{ marginBottom: '1rem' }}>
                <span style={{ color: 'var(--accent)', fontSize: '1.5rem' }}>★★★★★</span>
              </div>
              <p style={{ fontStyle: 'italic', marginBottom: '1rem', color: '#ccc' }}>
                "FACTO ELECTRONICAL has transformed our retail space with their premium LED solutions. 
                The energy savings have been remarkable!"
              </p>
              <div>
                <strong style={{ color: 'var(--text)' }}>Rahul Sharma</strong>
                <p style={{ color: '#999', fontSize: '0.9rem' }}>Retail Store Owner, Delhi</p>
              </div>
            </div>
            
            <div className="card">
              <div style={{ marginBottom: '1rem' }}>
                <span style={{ color: 'var(--accent)', fontSize: '1.5rem' }}>★★★★★</span>
              </div>
              <p style={{ fontStyle: 'italic', marginBottom: '1rem', color: '#ccc' }}>
                "Excellent product quality and outstanding customer service. 
                Their street lights have significantly improved our area's safety."
              </p>
              <div>
                <strong style={{ color: 'var(--text)' }}>Priya Patel</strong>
                <p style={{ color: '#999', fontSize: '0.9rem' }}>Municipal Contractor</p>
              </div>
            </div>
            
            <div className="card">
              <div style={{ marginBottom: '1rem' }}>
                <span style={{ color: 'var(--accent)', fontSize: '1.5rem' }}>★★★★★</span>
              </div>
              <p style={{ fontStyle: 'italic', marginBottom: '1rem', color: '#ccc' }}>
                "The best lighting solutions provider we've worked with. 
                Professional approach and competitive pricing."
              </p>
              <div>
                <strong style={{ color: 'var(--text)' }}>Amit Kumar</strong>
                <p style={{ color: '#999', fontSize: '0.9rem' }}>Distributor, Mumbai</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
